/**
 * useAppOperations.ts — data + action hooks for the App Operations Board
 * (ADR-075 step 5, D11; component-app-operations-board)
 *
 * The board is the per-app management surface for operators / tenant admins.
 * It READS deployment history (GET /apps/:app/deployments — the same endpoint
 * the propagation workflow uses), branch dev-states and health, and TRIGGERS
 * verification, change requests and UAT registration. It has NO approve or
 * push-back capability — approval is a tenant-store action (OQ1b); this module
 * deliberately imports nothing from useUatDecision.
 *
 * All requests carry the Keycloak Bearer token; every endpoint re-checks the
 * operator/tenant-admin role SERVER-SIDE (the SPA guard is UX only). The SPA
 * holds no GitHub / Doppler / Keycloak-admin credentials — those live behind
 * the broker API.
 */

import { useCallback, useEffect, useState } from 'react';
import type { TileConfig } from '../config/schema';
import { apiServiceToTile, type ApiService } from './useCatalog';

// ── Deployments (Part A) ─────────────────────────────────────────────────────

export interface BoardDeployment {
  deploy_no: number;
  deploy_date: string;
  release_tag: string;
  image_ref: string;
  status: 'uat' | 'published' | 'pushed_back' | 'superseded_published' | (string & {});
  queued: boolean;
  decided_by: string | null;
  decided_at: string | null;
  decision_comment: string | null;
}

export type BoardFetchState<T> =
  | { tag: 'loading' }
  | { tag: 'loaded'; data: T }
  | { tag: 'error'; errorCode?: string };

function useBoardFetch<T>(url: string | null, token: string | null, refreshKey = 0): BoardFetchState<T> {
  const [state, setState] = useState<BoardFetchState<T>>({ tag: 'loading' });
  useEffect(() => {
    if (!url || !token) return;
    let cancelled = false;
    setState({ tag: 'loading' });
    (async () => {
      try {
        const res = await fetch(url, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (!res.ok) {
          setState({ tag: 'error', errorCode: `HTTP_${res.status}` });
          return;
        }
        const data = (await res.json()) as T;
        if (!cancelled) setState({ tag: 'loaded', data });
      } catch {
        if (!cancelled) setState({ tag: 'error', errorCode: 'NETWORK' });
      }
    })();
    return () => { cancelled = true; };
  }, [url, token, refreshKey]);
  return state;
}

export function useDeployments(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<{ deployments: BoardDeployment[] }>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/deployments`, token, refreshKey,
  );
}

// ── Branches (Part A) ────────────────────────────────────────────────────────

export interface BoardBranch { branch: string; state: string; open: boolean }

export function useBranchState(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<{ app: string; branches: BoardBranch[] }>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/branch-state`, token, refreshKey,
  );
}

// ── Health & verification (Part B) ───────────────────────────────────────────

export interface HealthCheck {
  name: string;
  ok: boolean;
  status: number | null;
  latency_ms: number | null;
  detail?: string;
}
export interface AppHealth {
  app: string;
  overall: 'healthy' | 'degraded' | 'unreachable';
  checks: HealthCheck[];
}

export function useAppHealth(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<AppHealth>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/health`, token, refreshKey,
  );
}

export interface VerifyResult {
  pass: number; fail: number; failed_gates: string[];
}
export interface VerifyStatus {
  last: null | {
    dispatched_at: string;
    run_url?: string | null;
    result: VerifyResult | null;   // null while a run is still in flight
    raw_line?: string | null;
  };
}

export function useVerifyStatus(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<VerifyStatus>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/verify`, token, refreshKey,
  );
}

// ── Access management (Part D) ───────────────────────────────────────────────

export interface AccessUser {
  id: string;
  username: string;
  email: string;
  /** Tenant slug from the realm's `company` attribute; null = not recorded. */
  tenant: string | null;
  uat_approver: boolean;
  tester: boolean;
}

export function useAccessList(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<AccessListResponse>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/access`, token, refreshKey,
  );
}

// ── Actions (Parts B/C/D triggers) ───────────────────────────────────────────

export type ActionState =
  | { tag: 'idle' }
  | { tag: 'submitting'; action: string }
  | { tag: 'done'; action: string; message?: string }
  | { tag: 'error'; action: string; message: string };

/**
 * One shared action runner: POST/DELETE to a board endpoint, declared result
 * or declared failure — never a silent state. All board mutations audit
 * server-side (lifecycle_events / state_transition).
 */
export function useBoardAction(appId: string, token: string | null, onChanged?: () => void) {
  const [state, setState] = useState<ActionState>({ tag: 'idle' });

  const run = useCallback(async (
    action: string,
    path: string,
    opts?: { method?: 'POST' | 'DELETE'; body?: Record<string, unknown> },
  ): Promise<boolean> => {
    if (!token) { setState({ tag: 'error', action, message: 'not-authenticated' }); return false; }
    setState({ tag: 'submitting', action });
    try {
      const res = await fetch(
        `/api/broker/v1/apps/${encodeURIComponent(appId)}${path}`,
        {
          method: opts?.method ?? 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          ...(opts?.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
        },
      );
      const body = await res.json().catch(() => ({})) as { declared?: string; error?: string };
      if (!res.ok) {
        setState({ tag: 'error', action, message: body.error ?? `HTTP_${res.status}` });
        return false;
      }
      setState({ tag: 'done', action, message: body.declared });
      onChanged?.();
      return true;
    } catch {
      setState({ tag: 'error', action, message: 'NETWORK' });
      return false;
    }
  }, [appId, token, onChanged]);

  const reset = useCallback(() => setState({ tag: 'idle' }), []);
  return { state, run, reset };
}

// ── App summary (WP-2: App details + Deploy rollout need the catalogue row) ───

/**
 * The app's own catalogue row, read from the SAME endpoint and through the SAME
 * converter the marketplace uses. The board deliberately does not get a private
 * projection of "what a service is" — one source, one shape.
 *
 * The endpoint returns the whole catalogue and this picks one row out of it.
 * That is a deliberate trade: it reuses an authorised, already-deployed endpoint
 * and its server-side visibility rules instead of adding a per-app read whose
 * ADR-013 predicate would have to be re-derived and could drift.
 */
export function useAppSummary(appId: string, token: string | null, refreshKey = 0) {
  const state = useBoardFetch<{ services: ApiService[] }>(
    '/api/broker/v1/services', token, refreshKey,
  );
  if (state.tag !== 'loaded') return state as BoardFetchState<TileConfig | null>;
  const row = state.data.services.find((s) => s.id === appId);
  return {
    tag: 'loaded' as const,
    data: row ? apiServiceToTile(row) : null,
  };
}

// ── Access management, extended (WP-3: assign users to the app) ──────────────

/** One user the caller may assign to this app, with the tenant they belong to. */
export interface AssignableUser {
  id: string;
  username: string;
  email: string;
  /** Tenant slug from the realm's `company` attribute; null = not recorded. */
  tenant: string | null;
}

export type ScopeReason =
  | 'platform-scoped'
  | 'app-tenant-unknown'
  | 'tenant-claim-missing'
  | 'tenant-match'
  | 'tenant-mismatch';

export interface AccessListResponse {
  users: AccessUser[];
  /** The app's recorded tenant. null = none recorded (unknown, not unowned). */
  app_tenant: string | null;
  scope: { reason: ScopeReason; platform_scoped: boolean; caller_tenant: string | null };
  assignable: AssignableUser[];
  /** The grants the SERVER will accept. The UI offers these and nothing else. */
  assignable_grants: string[];
  /**
   * Users deliberately NOT offered, counted rather than silently dropped: a
   * board that shows six of nine users and says nothing is lying by omission.
   */
  hidden: { unattributed: number; other_tenant: number };
  realm_note: string;
  tenant_note: string;
}

// ── Billing & subscription (WP-4) ────────────────────────────────────────────

export interface BillingSubscription {
  id: string;
  plan_id: string;
  plan_name: string | null;
  status: string;
  approval_state: string;
  tenant_id: string | null;
  tenant_name: string | null;
  user_email: string | null;
  /** Always null: entitlements are not modelled. Never derived from a row count. */
  seats: null;
  started_at: string;
  trial_ends_at: string | null;
  suspended_at: string | null;
  grace_ends_at: string | null;
  ended_at: string | null;
  billing_status: string | null;
  notice_at: string | null;
  /** A DAY COUNT, never an amount. */
  notice_period_days: number | null;
  effective_cancel_at: string | null;
  closed_at: string | null;
}

export interface BillingResponse {
  app: string;
  /**
   * ALWAYS null in v1 — a SLOT, not a value.
   * open-task-2026-07-25-cost-disclosure-slot-empty is open: no rate model
   * exists anywhere in this system. The UI renders an explicit "no rate model
   * configured" state here and NO figure.
   * decision-2026-07-25-remove-fabricated-trial-cost is what a number here
   * would repeat.
   */
  rate_model: null;
  rate_model_note: string;
  subscriptions: BillingSubscription[];
  events: { at: string; event: string; detail: string | null }[];
  declared: string;
}

export function useAppBilling(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<BillingResponse>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/billing`, token, refreshKey,
  );
}
